import $merge from 'lodash.merge'

import {
  APPEND_COMMENT,
  APPEND_THREAD,
  RESET_INITIAL_STATE,
  SET_ACTIVE,
  SET_COMMENTS,
  SET_COMMENTS_VISIBILITY,
  SET_LOADER,
  SET_PARENT_ID,
  SET_OPTIONS,
  SET_THREADS,
  SET_WATCHING_STATUS,
} from './types'
import notifier from '~/utils/notifier'
import spoke from '~/utils/spoke'
import { runExcentricFile } from '~/utils/voicer'

const excentrics = runExcentricFile('store', 'comments')

const INITIAL_STATE = () => ({
  /**
   * @type {Boolean}
   * `true` if messager is currently loaded
   */
  isActive: false,

  /**
   * @type {Boolean}
   * `true` when a process is loading contents
   */
  isLoading: false,

  /**
   * @type {Boolean}
   */
  isWatching: false,

  /**
   * @type {Boolean}
   */
  isVisible: false,

  /**
   * @type {Object}
   * will be overrided by current content messager
   * options
   */
  options: {
    moderated: false,
    notify: true,
    reactable: true,
    subcomments: true,
  },

  /**
   * @type {ObjectID}
   * object id of the parent comment id
   */
  parentId: null,

  /**
   * @type {Array}
   */
  threads: [],

  /**
   * @type {Array}
   */
  comments: [],
})

export const state = () => ({
  ...$merge(INITIAL_STATE(), excentrics.state),
})

export const actions = $merge(
  {
    /**
     * This action will fetch comments of a given content
     * Content should be present in the `player.content` property
     * and accessible via `spoke.item`
     * @returns {Promise}
     *
     * @todo
     * unwatch messenger
     */
    getComments({ rootState, state, commit, dispatch }, useContent = null) {
      const notify = notifier(dispatch)

      // Set the UI to loading state
      commit(SET_LOADER, true)
      commit(SET_COMMENTS, [])
      commit(SET_THREADS, [])

      let content

      if (useContent === null) {
        content = spoke.item(rootState.player.content)
      } else {
        content = useContent
      }

      const messager = content.messager

      commit(SET_ACTIVE, true)
      commit(SET_OPTIONS, messager.options)

      messager
        .get() // return a pseudoQuery
        .on('comment', (comment) => {
          commit(APPEND_THREAD, comment.lean())
        })
        .on('success', (comments) => {
          commit(SET_COMMENTS, [])
          commit(SET_THREADS, comments.lean().toArray())
          commit(SET_LOADER, false)
        })
        .on('error', (error) => {
          notify('error', this.app.i18n.t('fetch_failed'), error)
          commit(SET_LOADER, false)
        })
    },

    reloadComments({ rootState, state, commit }, useContent = null) {
      let content

      if (useContent === null) {
        content = spoke.item(rootState.player.content)
      } else {
        content = useContent
      }

      const conversation = content.$conversations('comment')

      if (!conversation) {
        return
      }

      const commentsCollection = this.$spoke.collection('comments')
      commentsCollection.resource = `contents/${content.id}/conversations/${
        content.$conversations('comment').id
      }/comments`

      commentsCollection.get().on('success', (comments) => {
        const newthreads = comments.lean().toArray()

        // @todo: stupid
        if (newthreads.length > state.threads.length) {
          commit(SET_THREADS, newthreads)
        }

        // const outputComments = comments.lean().toArray()
        // const lastCommentId = $get(
        //   outputComments[outputComments.length - 1],
        //   'id'
        // )

        // for (let i = 0; i < outputComments.length; i++) {
        //   const mnewComment = outputComments[i]

        //   console.log(mnewComment.id, { lastCommentId })

        //   if (mnewComment.id === lastCommentId) {
        //     break
        //   }

        //   // append new comment
        //   console.log('append new comments here')
        // }
      })
    },

    /**
     * Post a comment as response or new thread
     * @param {String} message sent within /POST
     */
    async postComment({ rootState, state, commit, dispatch }, message) {
      const notify = notifier(dispatch)

      try {
        let data
        if (!state.parentId) {
          data = await spoke
            .item(rootState.player.content)
            .messager.send(message)
        } else {
          data = await spoke
            .item(rootState.player.content)
            .messager.thread(state.parentId)
            .send(message)
        }
        notify('success', this.app.i18n.t('comment_added'))
        return data
      } catch (error) {
        console.error(error)
        notify('error', this.app.i18n.t('impossible_comment_add'), error)
      }
    },

    /**
     * Switch the comment modal opened or closed
     */
    async toggleCommentsVisibility({ state, rootState, commit, dispatch }) {
      const isVisible = !state.isVisible
      await commit(SET_COMMENTS_VISIBILITY, isVisible)

      // applyied once mutation is effetive
      if (isVisible) {
        dispatch('getComments')
      } else {
        // unwatch previous opened messager and destroy
        spoke.item(rootState.player.content).messager.destroy()
        commit(SET_ACTIVE, false)
      }
    },

    destroyMessager({ rootState, commit }) {
      spoke.item(rootState.player.content).messager.destroy()
      commit(SET_ACTIVE, false)
    },

    /**
     * Switch between threads and responses
     * @param {String} type can be 'threads' or 'responses'
     * @todo
     * unwatch messenger
     */
    toggleCommentType({ commit, rootState, state, dispatch }, parentId = null) {
      const notify = notifier(dispatch)

      const currentParentId = state.parentId
      const messager = spoke.item(rootState.player.content).messager

      const oldThread = parentId ? messager.thread(parentId) : null
      commit(SET_PARENT_ID, parentId)

      if (parentId) {
        commit(SET_LOADER, true)
        const thread = messager.thread(parentId)

        thread
          .on('comment', (comment) => {
            commit(APPEND_COMMENT, comment.lean())
          })
          .get() // return a pseudoQuery
          .on('success', (comments) => {
            commit(SET_COMMENTS, comments.lean().toArray())
            commit(SET_LOADER, false)
          })
          .on('error', (error) => {
            notify('error', null, error)
            commit(SET_LOADER, false)
          })
      } else if (!parentId && currentParentId) {
        // todo: unwatch previous thread
        commit(SET_COMMENTS, [])
        commit(SET_LOADER, false)

        // unwatch previous opened thread
        if (oldThread) {
          oldThread.unwatch()
        }
      }
    },

    resetState({ commit }) {
      commit(RESET_INITIAL_STATE)
    },
  },
  excentrics.actions
)

export const mutations = $merge(
  {
    /**
     * Display a loading state to any comments modal
     * @param {Boolean} status
     */
    [SET_ACTIVE](state, status) {
      state.isActive = status
    },

    /**
     * Display a loading state to any comments modal
     * @param {Boolean} status
     */
    [SET_LOADER](state, status) {
      state.isLoading = status
    },

    /**
     * Append a new comment in the `comments` list
     * @param {Object} comment
     */
    [APPEND_COMMENT](state, comment) {
      const comments = [...state.comments, comment]

      state.comments = comments
    },

    /**
     * Append a new thread in the `threads` list
     * @param {Object} thread
     */
    [APPEND_THREAD](state, thread) {
      const threads = [...state.threads, thread]

      state.threads = threads
    },

    /**
     * Add sub comments (issued from an active thread)
     * @param {Object} comments
     */
    [SET_COMMENTS](state, comments) {
      state.comments = comments
    },

    /**
     * Set current messager options
     * @param {Object} options
     */
    [SET_OPTIONS](state, options) {
      state.options = Object.assign({}, options)
    },

    /**
     * Add mains threads
     * @param {Object} threads
     */
    [SET_THREADS](state, threads) {
      state.threads = threads
    },

    /**
     * Define if the comment modal is open or not
     * @param {Boolean} status
     */
    [SET_COMMENTS_VISIBILITY](state, status) {
      state.isVisible = status
    },

    /**
     * Define if the modal shows threads or responses
     * @param {String} type
     */
    [SET_PARENT_ID](state, parentId) {
      state.parentId = parentId
    },

    /**
     * Define if we are currently watching something
     * @param {Boolean} status
     */
    [SET_WATCHING_STATUS](state, status) {
      state.isWatching = status
    },

    /**
     * reset vuex comments store on initial state
     */
    [RESET_INITIAL_STATE](state) {
      Object.assign(state, INITIAL_STATE())
    },
  },
  excentrics.mutations
)

export const getters = $merge(
  {
    /**
     * current messager options
     * @returns {Object} options
     */
    currentOptions(state) {
      return state.options
    },
    /**
     * Shows the current threads displayed on responses modal
     * @returns {Object}
     */
    currentThread(state) {
      return state.threads.find((thread) => thread.id === state.parentId)
    },

    currentThreadResponses(state) {
      return state.comments
    },

    /**
     * @returns {Boolean}
     */
    isLoading(state) {
      return state.isLoading
    },

    /**
     * @returns {Boolean}
     */
    isVisible(state) {
      return state.isVisible
    },

    /**
     * @returns {String} comment id
     */
    parentId(state) {
      return state.parentId
    },

    /**
     * @returns {Array} comments
     */
    threads(state) {
      return state.threads
    },
  },
  excentrics.getters
)
